import {
  type BatchDeleteInboxConversationsMutation,
  type FetchResult,
  type MutationResult,
  useBatchDeleteInboxConversationsMutation,
} from "@qw/graphql";
import { useOpenConfirmation, useToast } from "@qw/ui";
import { useCallback } from "react";

import { useInboxQueryStates } from "./inbox-query-states-provider";

type UseBatchDeleteConversationsReturn = [
  (conversationIds: string[]) => Promise<FetchResult<BatchDeleteInboxConversationsMutation> | undefined>,
  MutationResult<BatchDeleteInboxConversationsMutation>,
];

export const useBatchDeleteConversations = (): UseBatchDeleteConversationsReturn => {
  const { toast } = useToast();
  const { openConfirmation } = useOpenConfirmation();
  const { conversationId: currentConversationId, setConversationId: setCurrentConversationId } = useInboxQueryStates();

  const [batchDeleteConversationsMutation, result] = useBatchDeleteInboxConversationsMutation({
    refetchQueries: ["InboxConversations", "InboxLabelUnreadCounts"],
  });

  const batchDeleteConversations = useCallback(
    async (conversationIds: string[]) => {
      const confirmed = await openConfirmation({
        title: `Delete ${conversationIds.length} conversations`,
        description: "Are you sure you want to delete these conversations? This action cannot be undone.",
        buttonProps: { theme: "destructive", children: "Delete" },
      });

      if (!confirmed) {
        return;
      }

      return batchDeleteConversationsMutation({
        variables: { input: { conversationIds } },
        onCompleted: (data) => {
          if (!data?.batchDeleteInboxConversations) {
            throw new Error("Failed to delete conversations");
          }

          if (currentConversationId && conversationIds.includes(currentConversationId)) {
            setCurrentConversationId(null, { history: "replace" });
          }

          toast({
            variant: "default",
            title: `Deleted ${conversationIds.length} conversations`,
          });
        },
        onError: (error) => {
          toast({
            title: `Failed to delete ${conversationIds.length} conversations`,
            description: error instanceof Error ? error.message : "An unexpected error occurred",
            variant: "destructive",
          });
        },
      });
    },
    [batchDeleteConversationsMutation, openConfirmation, toast, currentConversationId, setCurrentConversationId]
  );

  return [batchDeleteConversations, result];
};
